import React, { useState } from 'react';
import {
  Box, Container, Grid, Typography, Paper, Stack,
  useTheme, useMediaQuery, Card, CardContent,
  Fade, Zoom, Grow, Slide, alpha, Chip, Divider,
  Button, IconButton, Avatar, List, ListItem,
  ListItemIcon, ListItemText, Tab, Tabs
} from '@mui/material';
import { styled, keyframes } from '@mui/material/styles';
import HomeFooter from '../HomeFooter';
import Navbar from '../Navbar.jsx';

import {
  Map,
  Route,
  Navigation,
  LocationOn,
  Timeline,
  Speed,
  Analytics,
  Layers,
  GpsFixed,
  MyLocation,
  DirectionsCar,
  LocalShipping,
  Store,
  CheckCircle,
  ArrowForward,
  PlayArrow,
  Download,
  Share,
  Settings,
  Cloud,
  Security,
  Sync,
  Devices,
  FilterList,
  Search,
  PinDrop,
  ZoomOutMap,
  LayersClear,
  Traffic,
  Schedule,
  Notifications
} from '@mui/icons-material';

// Animations
const floatAnimation = keyframes`
  0%, 100% { transform: translateY(0px) rotate(0deg); }
  25% { transform: translateY(-15px) rotate(2deg); }
  50% { transform: translateY(-10px) rotate(0deg); }
  75% { transform: translateY(-15px) rotate(-2deg); }
`;

const pulseGlow = keyframes`
  0%, 100% { 
    box-shadow: 0 0 20px rgba(66, 133, 244, 0.3);
  }
  50% { 
    box-shadow: 0 0 40px rgba(66, 133, 244, 0.5);
  }
`;

const mapFlow = keyframes`
  0% { stroke-dashoffset: 1000; }
  100% { stroke-dashoffset: 0; }
`;

// Styled Components
const MapHero = styled(Box)(({ theme }) => ({
  minHeight: '76vh',
  background: `linear-gradient(135deg, #f0f4f8 0%, #e2e8f0 100%)`, // Changed to light background
  position: 'relative',
  overflow: 'hidden',
  '&::before': {
    content: '""',
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    backgroundImage: `
      radial-gradient(circle at 10% 20%, ${alpha('#4285f4', 0.1)} 0%, transparent 50%),
      radial-gradient(circle at 90% 80%, ${alpha('#34a853', 0.1)} 0%, transparent 50%)
    `,
  }
}));

const MapCard = styled(Card)(({ theme }) => ({
  background: 'rgba(255, 255, 255, 0.8)', // Light card background
  backdropFilter: 'blur(20px)',
  borderRadius: '24px',
  border: `1px solid ${alpha('#4285f4', 0.2)}`,
  boxShadow: '0 10px 40px rgba(0, 0, 0, 0.05)',
  transition: 'all 0.4s cubic-bezier(0.175, 0.885, 0.32, 1.275)',
  overflow: 'hidden',
  position: 'relative',
  '&::before': {
    content: '""',
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    height: '4px',
    background: 'linear-gradient(90deg, #4285f4, #34a853)',
  },
  '&:hover': {
    transform: 'translateY(-12px)',
    borderColor: '#4285f4',
    boxShadow: '0 25px 60px rgba(66, 133, 244, 0.15)',
    '& .map-icon': {
      transform: 'scale(1.1) rotate(10deg)',
      animation: `${pulseGlow} 1.5s ease-in-out infinite`,
    }
  }
}));

const MapFeature = styled(Box)(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  gap: 3,
  padding: '20px',
  borderRadius: '16px',
  background: alpha('#4285f4', 0.05),
  border: `1px solid ${alpha('#4285f4', 0.1)}`,
  transition: 'all 0.3s ease',
  '&:hover': {
    background: alpha('#4285f4', 0.1),
    borderColor: '#4285f4',
    transform: 'translateX(10px)',
  }
}));

const MapPin = styled(Box)(({ theme, color, size }) => ({
  width: size || 40,
  height: size || 40,
  borderRadius: '50%',
  background: alpha(color || '#4285f4', 0.1),
  border: `2px solid ${color || '#4285f4'}`,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  color: color || '#4285f4',
  position: 'absolute',
  animation: `${floatAnimation} 4s ease-in-out infinite`,
  cursor: 'pointer',
  '&::after': {
    content: '""',
    position: 'absolute',
    width: '100%',
    height: '100%',
    borderRadius: '50%',
    background: alpha(color || '#4285f4', 0.2),
    animation: `${pulseGlow} 2s ease-in-out infinite`,
  }
}));

const Circuit = () => {
  const theme = useTheme();
  const homepage = `${process.env.PUBLIC_URL}/images/map1.jpg`;
  const map = `${process.env.PUBLIC_URL}/images/Map.png`;

  const features = [
    {
      icon: <Route />,
      title: 'Route Planning & Optimization',
      description: 'Intelligent route planning that calculates the most efficient paths for your fleet',
      color: '#4285f4'
    },
    {
      icon: <Navigation />,
      title: 'Real-time Navigation',
      description: 'Turn-by-turn navigation with live traffic updates and alternative routes',
      color: '#34a853'
    },
    {
      icon: <Timeline />,
      title: 'Distance Optimization',
      description: 'Smart algorithms that prioritize shorter distances before longer ones',
      color: '#fbbc05'
    },
    {
      icon: <Analytics />,
      title: 'Performance Analytics',
      description: 'Detailed insights into route efficiency and delivery performance',
      color: '#ea4335'
    }
  ];

  const useCases = [
    {
      icon: <LocalShipping />,
      title: 'Logistics & Delivery',
      description: 'Optimize delivery routes for maximum efficiency and reduced fuel costs',
      color: '#4285f4'
    },
    {
      icon: <DirectionsCar />,
      title: 'Field Service Management',
      description: 'Manage multiple service technicians with optimized scheduling',
      color: '#34a853'
    },
    {
      icon: <Store />,
      title: 'Retail & Distribution',
      description: 'Streamline supply chain operations with intelligent route planning',
      color: '#fbbc05'
    },
    {
      icon: <GpsFixed />,
      title: 'Fleet Management',
      description: 'Track and optimize vehicle movements in real-time',
      color: '#ea4335'
    }
  ];

  const benefits = [
    'Reduces travel time by up to 30%',
    'Lowers fuel consumption and costs',
    'Improves delivery accuracy and speed',
    'Enhances customer satisfaction',
    'Real-time tracking and updates',
    'Scalable for businesses of all sizes'
  ];

  const mapPins = [
    { top: '30%', left: '20%', color: '#4285f4', size: 50, delay: '0s' },
    { top: '40%', left: '35%', color: '#34a853', size: 40, delay: '0.5s' },
    { top: '25%', left: '55%', color: '#fbbc05', size: 45, delay: '1s' },
    { top: '50%', left: '65%', color: '#ea4335', size: 35, delay: '1.5s' },
    { top: '35%', left: '75%', color: '#4285f4', size: 40, delay: '2s' },
  ];

  return (
    <Box sx={{
      background: '#f0f4f8', // Changed to light background
      color: '#0f172a', // Changed to dark text
      minHeight: '100vh',
      position: 'relative',
      overflow: 'hidden'
    }}>
      <Navbar />

      {/* Animated Map Background */}
      <Box sx={{
        position: 'absolute',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        pointerEvents: 'none',
        overflow: 'hidden',
        opacity: 0.1
      }}>
        <Box sx={{
          position: 'absolute',
          width: '200%',
          height: '200%',
          backgroundImage: `
            linear-gradient(90deg, transparent 79px, ${alpha('#4285f4', 0.1)} 79px, ${alpha('#4285f4', 0.1)} 81px, transparent 81px),
            linear-gradient(${alpha('#4285f4', 0.1)} 1px, transparent 1px)
          `,
          backgroundSize: '100px 100px',
          animation: 'moveGrid 20s linear infinite',
          '@keyframes moveGrid': {
            '0%': { transform: 'translate(0, 0)' },
            '100%': { transform: 'translate(-50px, -50px)' }
          }
        }} />
      </Box>

      {/* Hero Section */}
      <MapHero>
        <Container maxWidth="xl">
          <Grid container spacing={6} alignItems="center"  sx={{
        minHeight: { xs: 'auto', md: '78vh' },
        py: { xs: 4, md: 3 },
      }}>
            <Grid item xs={12} md={6}>
              <Fade in timeout={1000}>
                <Box>
                  <Chip
                    icon={<Map />}
                    label="ROUTE OPTIMIZATION PLATFORM"
                    sx={{
                      background: alpha('#4551be', 0.1), // Changed to blue accent
                      color: '#4551be', // Changed to blue text
                      border: `1px solid ${alpha('#4551be', 0.2)}`,
                      fontWeight: 600,
                      fontSize: '14px',
                      padding: '8px 16px',
                      mb: 4,
                      backdropFilter: 'blur(10px)'
                    }}
                  />

                  {/* Logo + Circuit Title in Same Row */}
                  <Stack
                    direction="row"
                    alignItems="center"
                    spacing={2}
                    sx={{ mb: 3 }}
                  >
                    {/* Logo */}
                    <Box
                      component="img"
                      src="/images/circuit_logo.png"
                      alt="Circuit Logo"
                      sx={{
                        width: { xs: 60, md: 80 },
                        height: { xs: 60, md: 80 },
                        objectFit: 'contain',
                        borderRadius: 2,
                        bgcolor: 'rgba(255,255,255,0.7)',
                        p: 0.5,
                        backdropFilter: 'blur(10px)',
                        border: '1px solid rgba(0,0,0,0.1)',
                      }}
                    />

                    {/* Title */}
                    <Typography sx={{
                      fontSize: { xs: '3rem', md: '4rem' },
                      fontWeight: 900,
                      lineHeight: 1.1,
                      color: '#4551be' // Changed to blue
                    }}>
                      Circuit
                    </Typography>
                  </Stack>

                  {/* Subtitle below */}
                  <Typography sx={{
                    color: '#475569', // Changed to dark grey
                    fontWeight: 400,
                    fontSize: { xs: '1.3rem', md: '1.8rem' },
                    mb: 3,
                    opacity: 0.9
                  }}>
                    Intelligent Route Planning & Optimization
                  </Typography>

                  <Typography sx={{
                    color: '#64748b', // Changed to dark grey
                    mb: 5,
                    fontSize: '1.1rem',
                    lineHeight: 1.6,
                    maxWidth: '90%'
                  }}>
                    Lider Map is a comprehensive route planning and optimization platform designed for
                    managing delivery and field service operations. Optimize routes, reduce travel time,
                    and improve operational efficiency.
                  </Typography>

                  {/* Quick Stats */}
                  {/* <Grid container spacing={4} sx={{ maxWidth: '600px' }}>
                    {[
                      { value: '30%', label: 'Travel Time Reduction', icon: <Speed /> },
                      { value: '25%', label: 'Fuel Cost Savings', icon: <Analytics /> },
                      { value: '99%', label: 'Route Accuracy', icon: <CheckCircle /> },
                      { value: '24/7', label: 'Real-time Tracking', icon: <Sync /> }
                    ].map((stat, index) => (
                      <Grid item xs={6} key={index}>
                        <Stack direction="row" alignItems="center" spacing={2}>
                          <Box sx={{
                            width: 50,
                            height: 50,
                            borderRadius: '12px',
                            background: alpha('#4551be', 0.1), // Changed to blue accent
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            color: '#4551be' // Changed to blue
                          }}>
                            {stat.icon}
                          </Box>
                          <Box>
                            <Typography variant="h4" sx={{
                              fontWeight: 900,
                              mb: 0.5,
                              color: '#1e293b' // Changed to dark text
                            }}>
                              {stat.value}
                            </Typography>
                            <Typography variant="body2" sx={{
                              color: '#64748b', // Changed to dark grey
                              fontWeight: 500
                            }}>
                              {stat.label}
                            </Typography>
                          </Box>
                        </Stack>
                      </Grid>
                    ))}
                  </Grid> */}
                </Box>
              </Fade>
            </Grid>

            <Grid item xs={12} md={6}>
              <Zoom in timeout={1000} style={{ transitionDelay: '300ms' }}>
                <Box sx={{
                  position: 'relative',
                  height: 500,
                  animation: `${floatAnimation} 8s ease-in-out infinite`
                }}>
                  {/* Interactive Map Visualization */}
                  {/* Interactive Map Visualization */}
<Paper sx={{
  borderRadius: '28px',
  border: '1px solid rgba(0, 0, 0, 0.05)',
  p: 4,
  height: '100%',
  overflow: 'hidden',
  bgcolor: 'white',
  position: 'relative', // ADDED - needed for absolute children
}}>
  {/* Map Background - INCREASED OPACITY, REMOVED BLUR */}
  <Box sx={{
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    backgroundImage: `url(${map})`,
    backgroundSize: 'cover',
    backgroundPosition: 'center',
    opacity: 0.85, // CHANGED from 0.1 to 0.85
    filter: 'none', // ADDED - remove any blur
  }} />

  {/* Animated Route Path */}
  <Box sx={{
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    zIndex: 2, // ADDED - keep above map
    '& svg': {
      width: '100%',
      height: '100%'
    }
  }}>
    <svg viewBox="0 0 800 400">
      <path
        d="M100,200 Q250,150 400,200 T700,200"
        stroke="#4285f4"
        strokeWidth="4"
        fill="none"
        strokeDasharray="1000"
        strokeDashoffset="1000"
        style={{ animation: `${mapFlow} 3s linear infinite` }}
      />
      <path
        d="M100,250 Q300,200 500,250 T700,250"
        stroke="#34a853"
        strokeWidth="3"
        fill="none"
        strokeDasharray="1000"
        strokeDashoffset="1000"
        style={{ animation: `${mapFlow} 4s linear infinite`, animationDelay: '0.5s' }}
      />
    </svg>
  </Box>

  {/* Floating Pins */}
  {mapPins.map((pin, index) => (
    <MapPin
      key={index}
      color={pin.color}
      size={pin.size}
      sx={{
        top: pin.top,
        left: pin.left,
        animationDelay: pin.delay,
        zIndex: 3, // ADDED - keep above map
      }}
    >
      <LocationOn />
    </MapPin>
  ))}

  {/* Map Controls */}
  <Box sx={{
    position: 'absolute',
    bottom: 20,
    right: 20,
    display: 'flex',
    gap: 2,
    zIndex: 4, // ADDED
  }}>
    <IconButton sx={{
      background: 'rgba(255, 255, 255, 0.95)',
      color: '#1e293b',
      boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
      '&:hover': { background: '#fff' }
    }}>
      <ZoomOutMap />
    </IconButton>
    <IconButton sx={{
      background: 'rgba(255, 255, 255, 0.95)',
      color: '#1e293b',
      boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
      '&:hover': { background: '#fff' }
    }}>
      <FilterList />
    </IconButton>
    <IconButton sx={{
      background: 'rgba(255, 255, 255, 0.95)',
      color: '#1e293b',
      boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
      '&:hover': { background: '#fff' }
    }}>
      <Traffic />
    </IconButton>
  </Box>

  {/* Route Info - SOLID WHITE BACKGROUND */}
  <Box sx={{
    position: 'absolute',
    top: 20,
    left: 20,
    background: '#ffffff', // CHANGED from rgba(255,255,255,0.9)
    borderRadius: '12px',
    padding: '16px',
    maxWidth: '300px',
    boxShadow: '0 4px 20px rgba(0,0,0,0.12)',
    zIndex: 4, // ADDED
    filter: 'none', // ADDED
    backdropFilter: 'none', // ADDED - remove blur
  }}>
    <Typography variant="h6" sx={{ fontWeight: 700, mb: 1, color: '#1e293b' }}>
      Optimized Route
    </Typography>
    <Typography variant="body2" sx={{ color: '#64748b' }}>
      5 stops • 42 miles • 1.5 hours
    </Typography>
    <Stack direction="row" spacing={2} sx={{ mt: 2 }}>
      <Chip label="Efficient" size="small" sx={{ background: '#34a853', color: '#fff' }} />
      <Chip label="Real-time" size="small" sx={{ background: '#4285f4', color: '#fff' }} />
    </Stack>
  </Box>
</Paper>
                </Box>
              </Zoom>
            </Grid>
          </Grid>
        </Container>
      </MapHero>

      {/* Key Features */}
      <Box sx={{ py: { xs: 8, md: 12 } }}>
        <Container maxWidth="xl">
          <Box sx={{ textAlign: 'center', mb: { xs: 8, md: 12 } }}>
            <Typography variant="h2" sx={{
              fontSize: { xs: '2.5rem', md: '3.5rem' },
              fontWeight: 900,
              mb: 3,
              color: '#1e293b' // Changed to dark text
            }}>
              Advanced Route Optimization
            </Typography>
            <Typography sx={{
              color: '#64748b', // Changed to dark grey
              maxWidth: '800px',
              mx: 'auto',
              lineHeight: 1.6,
              fontSize: '1.2rem'
            }}>
              Intelligent algorithms that prioritize efficiency, distance and time to deliver optimal routing solutions
            </Typography>
          </Box>

          <Grid container spacing={4}>
  {features.map((feature, index) => (
    <Grid
      item
      xs={12}
      sm={6}
      lg={3}
      key={index}
      sx={{
        display: "flex",
      }}
    >
      <Slide
        direction="up"
        in
        timeout={800}
        style={{
          transitionDelay: `${index * 100}ms`,
          width: "100%",
          height: "100%",
        }}
      >
        <MapCard
          sx={{
            width: "100%",
            height: "100%",
            display: "flex",
            flexDirection: "column",
          }}
        >
          <CardContent
            sx={{
              p: 4,
              textAlign: "center",
              flex: 1,
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
            }}
          >
            <Box
              sx={{
                width: 80,
                height: 80,
                minHeight: 80,
                borderRadius: "20px",
                background: alpha(feature.color, 0.1),
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                margin: "0 auto 24px auto",
                color: feature.color,
                transition: "all 0.4s ease",
              }}
              className="map-icon"
            >
              {feature.icon}
            </Box>

            <Typography
              variant="h5"
              sx={{
                fontWeight: 800,
                mb: 2,
                color: "#1e293b",
              }}
            >
              {feature.title}
            </Typography>

            <Typography
              sx={{
                color: "#64748b",
                lineHeight: 1.6,
                fontSize: "0.95rem",
              }}
            >
              {feature.description}
            </Typography>
          </CardContent>
        </MapCard>
      </Slide>
    </Grid>
  ))}
</Grid>
        </Container>
      </Box>

      {/* How It Works */}
      <Box sx={{
        py: { xs: 8, md: 12 },
        background: 'linear-gradient(135deg, #e2e8f0 0%, #cbd5e1 100%)' // Changed to light background
      }}>
        <Container maxWidth="xl">
          <Grid container spacing={6} alignItems="center">
            <Grid item xs={12} md={6}>
              <Fade in timeout={1000}>
                <Box>
                  <Typography variant="h2" sx={{
                    fontSize: { xs: '2.5rem', md: '3rem' },
                    fontWeight: 900,
                    mb: 4,
                    color: '#1e293b' // Changed to dark text
                  }}>
                    Intelligent Route Strategy
                  </Typography>

                  <Typography sx={{
                    color: '#64748b', // Changed to dark grey
                    mb: 5,
                    lineHeight: 1.7,
                    fontSize: '1.1rem'
                  }}>
                    Our platform employs a strategic approach that optimizes journeys based on distance
                    considerations. Starting with nearby locations and gradually progressing to farther
                    destinations, we ensure efficient and streamlined exploration.
                  </Typography>

                  <Stack spacing={4}>
                    {[
                      {
                        step: '1',
                        title: 'Strategic Pin Placement',
                        description: 'Multiple pins representing destinations organized for optimal journey planning'
                      },
                      {
                        step: '2',
                        title: 'Distance Prioritization',
                        description: 'Prioritizes shorter distances before gradually progressing to farther stops'
                      },
                      {
                        step: '3',
                        title: 'Efficient Progression',
                        description: 'Starts with nearby locations, then incorporates longer-distance destinations'
                      },
                      {
                        step: '4',
                        title: 'Continuous Optimization',
                        description: 'Real-time adjustments based on traffic, weather and road conditions'
                      }
                    ].map((step, index) => (
                      <MapFeature key={index}>
                        <Box sx={{
                          width: 50,
                          height: 50,
                          borderRadius: '12px',
                          background: alpha('#4285f4', 0.1),
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          color: '#4285f4',
                          fontWeight: 700,
                          fontSize: '1.2rem',
                          flexShrink: 0
                        }}>
                          {step.step}
                        </Box>
                        <Box>
                          <Typography variant="h6" sx={{
                            fontWeight: 700,
                            mb: 1,
                            color: '#1e293b' // Changed to dark text
                          }}>
                            {step.title}
                          </Typography>
                          <Typography sx={{
                            color: '#64748b', // Changed to dark grey
                            lineHeight: 1.6
                          }}>
                            {step.description}
                          </Typography>
                        </Box>
                      </MapFeature>
                    ))}
                  </Stack>
                </Box>
              </Fade>
            </Grid>

            <Grid item xs={12} md={6}>
              <Zoom in timeout={1000} style={{ transitionDelay: '300ms' }}>
                <Box>
                  <Paper sx={{
                    background: 'rgba(255, 255, 255, 0.8)', // Light card background
                    backdropFilter: 'blur(20px)',
                    borderRadius: '24px',
                    border: '1px solid rgba(0, 0, 0, 0.05)',
                    p: 4,
                    boxShadow: '0 20px 60px rgba(0, 0, 0, 0.05)'
                  }}>
                    <Typography variant="h5" gutterBottom sx={{
                      fontWeight: 700,
                      mb: 4,
                      color: '#4285f4'
                    }}>
                      Key Benefits
                    </Typography>

                    <List>
                      {benefits.map((benefit, index) => (
                        <ListItem key={index} sx={{ px: 0, py: 1.5 }}>
                          <ListItemIcon>
                            <CheckCircle sx={{ color: '#34a853' }} />
                          </ListItemIcon>
                          <ListItemText
                            primary={benefit}
                            primaryTypographyProps={{
                              sx: {
                                fontWeight: 500,
                                color: '#1e293b' // Changed to dark text
                              }
                            }}
                          />
                        </ListItem>
                      ))}
                    </List>

                    <Divider sx={{ my: 4, borderColor: 'rgba(0, 0, 0, 0.1)' }} />

                    <Typography variant="h6" gutterBottom sx={{
                      fontWeight: 700,
                      mb: 2,
                      color: '#1e293b' // Changed to dark text
                    }}>
                      Perfect For
                    </Typography>

                    <Grid container spacing={2}>
                      {useCases.map((useCase, index) => (
                        <Grid item xs={12} sm={6} key={index}>
                          <Box sx={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: 2,
                            p: 2,
                            borderRadius: '8px',
                            background: alpha(useCase.color, 0.05)
                          }}>
                            <Box sx={{
                              color: useCase.color,
                              display: 'flex',
                              alignItems: 'center'
                            }}>
                              {useCase.icon}
                            </Box>
                            <Typography variant="body2" sx={{ color: '#1e293b' }}>
                              {useCase.title}
                            </Typography>
                          </Box>
                        </Grid>
                      ))}
                    </Grid>
                  </Paper>
                </Box>
              </Zoom>
            </Grid>
          </Grid>
        </Container>
      </Box>

      {/* Use Cases */}
      <Box sx={{ py: { xs: 8, md: 12 } }}>
        <Container maxWidth="xl">
          <Box sx={{ textAlign: 'center', mb: { xs: 8, md: 12 } }}>
            <Typography variant="h2" sx={{
              fontSize: { xs: '2.5rem', md: '3rem' },
              fontWeight: 900,
              mb: 3,
              color: '#1e293b' // Changed to dark text
            }}>
              Industry Applications
            </Typography>
            <Typography sx={{
              color: '#64748b', // Changed to dark grey
              maxWidth: '800px',
              mx: 'auto',
              lineHeight: 1.6,
              fontSize: '1.2rem'
            }}>
              Widely used across various industries for efficient route management and optimization
            </Typography>
          </Box>

          <Grid container spacing={4}>
            {useCases.map((useCase, index) => (
              <Grid item xs={12} sm={6} lg={3} key={index}>
                <Grow in timeout={1000} style={{ transitionDelay: `${index * 150}ms` }}>
                  <MapCard>
                    <CardContent sx={{ p: 4, textAlign: 'center' }}>
                      <Box sx={{
                        width: 70,
                        height: 70,
                        borderRadius: '18px',
                        background: alpha(useCase.color, 0.1),
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        margin: '0 auto 20px auto',
                        color: useCase.color
                      }}>
                        {useCase.icon}
                      </Box>

                      <Typography variant="h6" sx={{
                        fontWeight: 700,
                        mb: 2,
                        color: '#1e293b' // Changed to dark text
                      }}>
                        {useCase.title}
                      </Typography>

                      <Typography sx={{
                        color: '#64748b', // Changed to dark grey
                        lineHeight: 1.6,
                        fontSize: '0.95rem'
                      }}>
                        {useCase.description}
                      </Typography>
                    </CardContent>
                  </MapCard>
                </Grow>
              </Grid>
            ))}
          </Grid>
        </Container>
      </Box>

      {/* CTA Section */}
      <Box sx={{
        py: { xs: 10, md: 15 },
        background: `linear-gradient(135deg, #f0f4f8 0%, #e2e8f0 100%)`, // Changed to light background
        color: '#0f172a', // Changed to dark text
        position: 'relative',
        overflow: 'hidden'
      }}>
        <Box sx={{
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundImage: `url(${homepage})`,
          backgroundSize: 'cover',
          backgroundPosition: 'center',
          opacity: 0.05
        }} />

        <Container maxWidth="xl" sx={{ position: 'relative', zIndex: 2 }}>
          <Box sx={{ textAlign: 'center' }}>
            <Box sx={{
              display: 'inline-flex',
              alignItems: 'center',
              justifyContent: 'center',
              width: 100,
              height: 100,
              borderRadius: '25px',
              background: alpha('#4551be', 0.1), // Changed to blue accent
              mb: 4,
              border: `2px solid ${alpha('#4551be', 0.2)}`,
              animation: `${pulseGlow} 3s ease-in-out infinite`
            }}>
              <Map sx={{ fontSize: 48, color: '#4551be' }} />
            </Box>

            <Typography variant="h2" sx={{
              fontSize: { xs: '2.5rem', md: '3.5rem' },
              fontWeight: 900,
              mb: 3,
              color: '#1e293b' // Changed to dark text
            }}>
              Optimize Your Routes Today
            </Typography>

            <Typography sx={{
              maxWidth: '800px',
              mx: 'auto',
              mb: 6,
              fontSize: '1.4rem',
              color: '#64748b', // Changed to dark grey
              lineHeight: 1.6,
              opacity: 0.9
            }}>
              Transform your delivery and field service operations with intelligent route planning.
              Reduce costs, improve efficiency and enhance customer satisfaction.
            </Typography>
          </Box>
        </Container>
      </Box>

      <HomeFooter />
    </Box>
  );
};

export default Circuit;